import { Button, ErrorBoundary, VStack } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { Image } from 'expo-image';
import React, { useCallback, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Animated,
  Modal,
  Pressable,
  SafeAreaView,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';

import { useDownloadFile } from '../../hooks/useDownloadFile';

export namespace ZoomableImageTypes {
  export type Props = {
    source: string;
  };
}

const styles = StyleSheet.create({
  image: {
    width: '100%',
    height: 200,
  },
  loadingContainer: {
    width: '100%',
    height: 200,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modal: {
    flex: 1,
  },
  modalContent: { flex: 1, padding: Utils.Token.ToInt(Spacing, 'inset.nano') },
  zoomImage: { height: '100%' },
});

export const ZoomableImage: React.FC<ZoomableImageTypes.Props> = ({ source }) => {
  const { height, width } = useWindowDimensions();
  const [isZooming, setIsZooming] = useState(false);
  const [scale, setScale] = useState(1);
  const { file, error, downloading } = useDownloadFile(source);
  let maxScale = height / width;
  let calcWidth = height;

  if (maxScale < 1.5) {
    maxScale = 1.5;
    calcWidth = height * maxScale;
  }

  const handleZoomIn = useCallback(() => {
    setIsZooming(true);
  }, []);

  const handleZoomOut = useCallback(() => {
    setIsZooming(false);
  }, []);

  const tapGesture = useMemo(
    () =>
      Gesture.Tap()
        .runOnJS(true)
        .numberOfTaps(2)
        .onStart(() => {
          setScale((oldScale) => (oldScale > 1 ? 1 : maxScale));
        }),
    [maxScale]
  );

  const pinchGesture = useMemo(
    () =>
      Gesture.Pinch()
        .runOnJS(true)
        .onUpdate((event) => {
          let newScale = event.scale < 1 ? 1 : event.scale;

          if (newScale > maxScale) {
            newScale = maxScale;
          }

          setScale(newScale);
        }),
    [maxScale]
  );

  if (downloading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" />
      </View>
    );
  }

  if (error) {
    return <ErrorBoundary errorMessage="Não foi possivel carregar a imagem" />;
  }

  return (
    <>
      <Pressable onPress={handleZoomIn}>
        <Image style={styles.image} contentFit="contain" source={file} cachePolicy="none" />
      </Pressable>
      <Modal visible={isZooming}>
        <SafeAreaView style={styles.modal}>
          <VStack flex={1} space={Spacing['stack.nano']} padding={Spacing['inset.nano']}>
            <Animated.ScrollView style={styles.modalContent} horizontal>
              <GestureDetector gesture={pinchGesture}>
                <GestureDetector gesture={tapGesture}>
                  <Image
                    style={[
                      styles.zoomImage,
                      {
                        width: calcWidth * scale,
                      },
                    ]}
                    source={file}
                    contentFit="contain"
                    cachePolicy="none"
                  />
                </GestureDetector>
              </GestureDetector>
            </Animated.ScrollView>
            <Button onPress={handleZoomOut}>Fechar</Button>
          </VStack>
        </SafeAreaView>
      </Modal>
    </>
  );
};
